import React, { useState, useMemo } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  IconButton, 
  Paper, 
  TextField, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Breadcrumbs,
  Link,
  Snackbar,
  Alert
} from '@mui/material';
import { 
  Home as HomeIcon, 
  PersonAdd, 
  Edit, 
  Delete, 
  Search, 
  ChevronRight,
  Person as PersonIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { db } from './Dexie';
import { useLiveQuery } from 'dexie-react-hooks';

function Users() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  // Ottieni gli utenti da Dexie
  const users = useLiveQuery(() => db.users.toArray()) || [];

  // Filtraggio utenti
  const filteredUsers = useMemo(() => {
    return users.filter(u => 
      u.username.toLowerCase().includes(searchQuery.toLowerCase()) || 
      u.email.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [users, searchQuery]);

  const handleOpenModal = (user = null) => {
    setSelectedUser(user || { username: '', email: '', role: 'User', status: 'Active' });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  const handleSaveUser = async () => {
    try {
      if (selectedUser.id) {
        await db.users.put(selectedUser);
        setToast({ open: true, message: 'Utente aggiornato con successo', severity: 'success' });
      } else {
        await db.users.add({ ...selectedUser, lastLogin: 'Mai' });
        setToast({ open: true, message: 'Nuovo utente creato con successo', severity: 'success' });
      }
      handleCloseModal();
    } catch (error) {
      setToast({ open: true, message: 'Errore durante il salvataggio', severity: 'error' });
    }
  };

  const handleDeleteUser = async (id) => {
    if (window.confirm('Sei sicuro di voler eliminare questo utente?')) {
      await db.users.delete(id);
      setToast({ open: true, message: 'Utente eliminato', severity: 'info' });
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Breadcrumbs */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Breadcrumbs separator={<ChevronRight fontSize="small" />} sx={{ color: 'rgba(255,255,255,0.7)' }}>
          <Link
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: 'inherit' }}
            onClick={() => navigate('/')}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Typography color="primary.main" sx={{ fontWeight: 600 }}>Gestione Utenti</Typography>
        </Breadcrumbs>
        
        <Button 
          variant="contained" 
          startIcon={<PersonAdd />} 
          onClick={() => handleOpenModal()}
        >
          Aggiungi Utente
        </Button>
      </Box>

      {/* Search Header */}
      <Paper sx={{ p: 3, mb: 4, backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Cerca per nome utente o email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: 'rgba(255,255,255,0.5)' }} />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Users Table */}
      <TableContainer component={Paper} sx={{ backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: 2 }}>
        <Table>
          <TableHead sx={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
            <TableRow>
              <TableCell>Utente</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Ruolo</TableCell>
              <TableCell>Stato</TableCell>
              <TableCell>Ultimo Accesso</TableCell>
              <TableCell align="right">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, opacity: 0.5 }}>
                  Nessun utente trovato.
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                        {user.username.charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography variant="subtitle2">{user.username}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip label={user.role} size="small" variant="outlined" color="primary" />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={user.status} 
                      size="small" 
                      color={user.status === 'Active' ? 'success' : 'default'} 
                    />
                  </TableCell>
                  <TableCell variant="caption">{user.lastLogin}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="primary" onClick={() => handleOpenModal(user)}>
                      <Edit />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDeleteUser(user.id)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal Aggiungi/Modifica */}
      <Dialog open={isModalOpen} onClose={handleCloseModal} fullWidth maxWidth="xs">
        <DialogTitle>{selectedUser?.id ? 'Modifica Utente' : 'Nuovo Utente'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            <TextField
              label="Nome Utente"
              fullWidth
              value={selectedUser?.username || ''}
              onChange={(e) => setSelectedUser({ ...selectedUser, username: e.target.value })}
            />
            <TextField
              label="Email"
              fullWidth
              value={selectedUser?.email || ''}
              onChange={(e) => setSelectedUser({ ...selectedUser, email: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Ruolo</InputLabel>
              <Select
                value={selectedUser?.role || 'User'}
                label="Ruolo"
                onChange={(e) => setSelectedUser({ ...selectedUser, role: e.target.value })}
              >
                <MenuItem value="Admin">Admin</MenuItem>
                <MenuItem value="Editor">Editor</MenuItem>
                <MenuItem value="User">User</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Stato</InputLabel>
              <Select
                value={selectedUser?.status || 'Active'}
                label="Stato"
                onChange={(e) => setSelectedUser({ ...selectedUser, status: e.target.value })}
              >
                <MenuItem value="Active">Attivo</MenuItem>
                <MenuItem value="Inactive">Inattivo</MenuItem>
                <MenuItem value="Suspended">Sospeso</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseModal}>Annulla</Button>
          <Button variant="contained" onClick={handleSaveUser}>Salva</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default Users;
